import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnDestroy, OnInit, Renderer2, signal } from "@angular/core";
import { tap, BehaviorSubject, Subscription } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { SortColumn } from "src/app/models/sortColumn.model";
import { ITablePageInfo } from "src/app/models/componentInputs/tablePageInfo.interface";
import { ITableColumn } from "src/app/models/tableColumns";
import { aiInLineControlTableColumns } from "src/app/models/tableColumns/aiInLineControlTableColumn";
import { ActiveFiltersHeightService } from "src/app/services/infra/activeFiltersHeight/active-filters-height.service";
import { AiInLineControlHttpService } from "src/app/services/httpServices/detection/aiInLineControlHttpService/ai-in-line-control-http.service";
import { InLineControlModel } from "src/app/models/api/inLineControl.model";
import { InLineControlTableRowViewModel } from "src/app/models/viewModels/inLineControlTableRowViewModel";
import { formatFromDate } from "src/app/common/datetime_utility";
import { LabelsProviderPipe } from "src/app/pipes/labels-provider/labels-provider.pipe";
import { LoggerService } from "src/app/services/infra/logger/logger.service";
import { getAiServiceLogoPath } from "src/app/common/aiServiceUtils";
import { HttpErrorResponse } from "@angular/common/http";
import { HttpErrorHandlerService } from "src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service";
import { JsonHalCollection, JsonHalCollectionPageInfo, JsonHalLinks } from "src/app/models/api/jsonhal.model";
import { Stack } from "src/app/common/dataStructures/stack";
import { IPaginationNavigationModel } from "src/app/models/paginationNavigation.model";
import { PaginationOperationEnum } from "src/app/components/table/table.component";
import { AiService } from "src/app/models/api/agentStep.model";

@Component({
    selector: "app-enterprise-ai-in-line-control",
    templateUrl: "./app-enterprise-ai-in-line-control.component.html",
    styleUrl: "./app-enterprise-ai-in-line-control.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppEnterpriseAiInLineControlComponent implements OnInit, OnDestroy {
    public columns = signal<ITableColumn[]>(aiInLineControlTableColumns);
    public inLineControlCurrentPage$ = new BehaviorSubject<InLineControlTableRowViewModel[]>([]);
    public isLoading = signal<boolean>(true);
    public activeTabIndex = 0;
    public totalItems = 0;
    public showNoData = false;
    public inLineControlData: InLineControlModel[] = [];
    public readonly defaultPageSize: number;
    public pageInfo$: BehaviorSubject<ITablePageInfo>;
    public sortedColumn$ = new BehaviorSubject<SortColumn | undefined>(undefined);
    private _paginationNavigation: IPaginationNavigationModel;
    private _sortOnChangeSubscription$: Subscription;
    private _firstPageSubscription$: Subscription;
    private _fetchByPageSubscription$: Subscription;
    private _notAvailableString: string;

    constructor(
        private _renderer: Renderer2,
        private _el: ElementRef,
        private _destroyRef: DestroyRef,
        private _activeFiltersHeightService: ActiveFiltersHeightService,
        private _aiInLineControlHttpService: AiInLineControlHttpService,
        private _labelsProviderPipe: LabelsProviderPipe,
        private _logger: LoggerService,
        private _httpErrorHandlerService: HttpErrorHandlerService,
    ) {
        this.defaultPageSize = this._aiInLineControlHttpService.defaultPageSize;
        this._notAvailableString = this._labelsProviderPipe.transform("common.notAvailable");
        this._initializePageInfo();
    }

    ngOnInit(): void {
        this._activeFiltersHeightService.height$
            .pipe(
                tap((height: number) => this._renderer.setStyle(this._el.nativeElement, "--applied-filters-height", `${height}px`, 1)),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe();

        // Set default sort by timestamp in descending order
        const defaultSort: SortColumn = {
            column: this.columns().find((col) => col.sortKey === "timestamp")!,
            isDesc: true,
        };
        this.sortedColumn$.next(defaultSort);

        this._subscribeToSortOnChange();
        this.fetchInLineControlData();
    }

    ngOnDestroy(): void {
        if (this._sortOnChangeSubscription$) {
            this._sortOnChangeSubscription$.unsubscribe();
        }
        if (this._firstPageSubscription$) {
            this._firstPageSubscription$.unsubscribe();
        }
        if (this._fetchByPageSubscription$) {
            this._fetchByPageSubscription$.unsubscribe();
        }
    }

    public fetchInLineControlData(): void {
        this._fetchFirstPage();
    }

    public onColumnSortClick(sortData: SortColumn): void {
        this.sortedColumn$.next(sortData);
    }

    public onPagination(operation: PaginationOperationEnum) {
        if (!this.isLoading()) {
            if (operation == PaginationOperationEnum.next) {
                this._paginationNavigation.previousPage.push(this._paginationNavigation.currentPage);
            }

            const pageUrl =
                operation == PaginationOperationEnum.next ? this._paginationNavigation.nextPage : this._paginationNavigation.previousPage.pop();
            if (pageUrl) {
                this._doPaginationWhenTokenExist(pageUrl);
            }
        }
    }

    private _subscribeToSortOnChange() {
        this._sortOnChangeSubscription$ = this.sortedColumn$.subscribe(() => {
            this._fetchFirstPage();
        });
    }

    private _fetchFirstPage() {
        this._cancelOngoingHttpRequest();
        this._tableBehaviorChangeBeforeFetch();

        this._firstPageSubscription$ = this._aiInLineControlHttpService.getInLineControls().subscribe({
            next: (response: InLineControlModel[]) => {
                const collection: JsonHalCollection<InLineControlModel> = {
                    _embedded: { items: response },
                    _links: {
                        self: { href: "" },
                        next: { href: "" },
                    },
                    page: {
                        number: 1,
                        size: response.length,
                        totalItems: response.length,
                        totalPages: 1,
                    },
                };
                this._handleInLineControlCollectionResponse(collection);
            },
            error: (err: HttpErrorResponse) => this._fetchRequestOnError(err),
        });
    }

    private _fetchByPage(continuationToken: string) {
        this._cancelOngoingHttpRequest();
        this._tableBehaviorChangeBeforeFetch();

        this._fetchByPageSubscription$ = this._aiInLineControlHttpService.getInLineControlsByContinuationToken(continuationToken).subscribe(
            (response) => this._handleInLineControlCollectionResponse(response),
            (error) => this._fetchRequestOnError(error),
        );
    }

    private _handleInLineControlCollectionResponse(response: JsonHalCollection<InLineControlModel>) {
        this._handlePaginationUrls(response._links);
        this._handlePageInfo(response.page);

        const items = response._embedded.items;

        if (!items || items.length == 0) {
            this.showNoData = true;
            this.inLineControlCurrentPage$.next([]);
        } else {
            this.showNoData = false;
            let viewModels = items.map((item) => this._transformToViewModel(item));

            // Apply sorting if a sort column is selected
            const currentSort = this.sortedColumn$.value;
            if (currentSort) {
                viewModels = this._sortViewModels(viewModels, currentSort);
            }

            this.inLineControlCurrentPage$.next(viewModels);
        }
        this.isLoading.set(false);
    }

    private _sortViewModels(viewModels: InLineControlTableRowViewModel[], sortData: SortColumn): InLineControlTableRowViewModel[] {
        return [...viewModels].sort((a, b) => {
            const aValue = a[sortData.column.sortKey as keyof InLineControlTableRowViewModel];
            const bValue = b[sortData.column.sortKey as keyof InLineControlTableRowViewModel];

            if (aValue === undefined || bValue === undefined) return 0;

            // Handle timestamp sorting
            if (sortData.column.sortKey === "timestamp") {
                const aDate = new Date(aValue as string).getTime();
                const bDate = new Date(bValue as string).getTime();
                return sortData.isDesc ? bDate - aDate : aDate - bDate;
            }

            // Handle string sorting
            if (typeof aValue === "string" && typeof bValue === "string") {
                return sortData.isDesc ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
            }

            // Handle number sorting
            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortData.isDesc ? bValue - aValue : aValue - bValue;
            }

            return 0;
        });
    }

    private _cancelOngoingHttpRequest() {
        if (this._firstPageSubscription$) {
            this._firstPageSubscription$.unsubscribe();
        }

        if (this._fetchByPageSubscription$) {
            this._fetchByPageSubscription$.unsubscribe();
        }
    }

    private _fetchRequestOnError(error: HttpErrorResponse) {
        this.inLineControlCurrentPage$.next([]);
        this.isLoading.set(false);
        this.showNoData = true;
        this._httpErrorHandlerService.handle(error);
    }

    private _tableBehaviorChangeBeforeFetch() {
        this.inLineControlCurrentPage$.next([]);
        this.isLoading.set(true);
    }

    private _handlePaginationUrls(links: JsonHalLinks) {
        if (!this._paginationNavigation) {
            this._paginationNavigation = {
                currentPage: links.self.href,
                nextPage: links.next?.href,
                previousPage: new Stack<string>(),
            } as IPaginationNavigationModel;
        } else {
            this._paginationNavigation.currentPage = links.self.href;
            this._paginationNavigation.nextPage = links.next?.href;
        }
    }

    private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo) {
        this.totalItems = pageInfo.totalItems;
        const tablePageInfo = {
            number: pageInfo.number,
            currSize: pageInfo.size,
            maxSize: this.defaultPageSize,
            totalItems: pageInfo.totalItems,
            totalPages: pageInfo.totalPages,
        };

        if (!this.pageInfo$) {
            this.pageInfo$ = new BehaviorSubject(tablePageInfo);
        } else {
            this.pageInfo$.next(tablePageInfo);
        }
    }

    private _doPaginationWhenTokenExist(url: string) {
        const continuationToken = this._tokenExtractor(url);

        if (continuationToken) {
            this._fetchByPage(continuationToken);
        }
    }

    private _tokenExtractor(url: string): string | null {
        const continuationToken = new URLSearchParams(url.slice(url.indexOf("?"))).get("continuationToken");
        if (!continuationToken) {
            this._logger.error("Failed to extract continuation token from json hal next activity resource page link");
            return null;
        }

        return continuationToken;
    }

    private _transformToViewModel(row: InLineControlModel): InLineControlTableRowViewModel {
        return {
            rowId: row.id,
            id: row.id,
            ruleName: row.ruleName ? row.ruleName : "",
            ruleId: row.ruleId,
            preventionType: row.preventionType === "" ? "Detected" : row.preventionType,
            preventionTypeIcon: this._getPreventionIcon(row.preventionType),
            timestamp: row.timestamp ? formatFromDate(row.timestamp) : this._notAvailableString,
            agent: row.agent ? row.agent : "",
            actor: row.actor ? row.actor : "",
            threadId: row.threadId ? row.threadId : "",
            clickable: false,
            aiServiceIcon: getAiServiceLogoPath(row.aiService),
        };
    }

    /*
    private _transformToViewModel(row: InLineControlModel): InLineControlTableRowViewModel {
        return {
            rowId: row.id,
            id: row.id,
            ruleName: row.rule?.displayName ?? "",
            ruleId: row.rule?.id ?? "",
            preventionType: row.rule?.action === "block" ? "Blocked" : "Detected",
            preventionTypeIcon: this._getPreventionIcon(row.rule?.action),
            timestamp: row.timestamp ? formatFromDate(row.timestamp) : this._notAvailableString,
            agent: row.relatedSteps?.[0]?.context?.agent?.id ?? "",
            actor: row?.mainActor?.id ?? "",
            threadId: row?.thread?.id ?? "",
            clickable: false,
            aiServiceIcon: getAiServiceLogoPath("m365Copilot" as AiService),
        };
    }
    */

    private _getPreventionIcon(preventionType: string): string {
        switch (preventionType) {
            case "blocked":
                return "assets/images/dataflow-icons/blocked.svg";
            case "detected":
                return "assets/images/dataflow-icons/shield-alert.svg";
            default:
                return "";
        }
    }

    /*
    private _getPreventionIcon(preventionType: string): string {
        switch (preventionType) {
            case "block":
                return "assets/images/dataflow-icons/blocked.svg";
            case "detect":
                return "assets/images/dataflow-icons/shield-alert.svg";
            default:
                return "assets/images/dataflow-icons/shield-alert.svg";
        }
    }
    */

    private _initializePageInfo() {
        this.pageInfo$ = new BehaviorSubject({
            number: 1,
            currSize: 0,
            maxSize: this._aiInLineControlHttpService.defaultPageSize,
            totalItems: 0,
            totalPages: 1,
        });
    }
}
